import { z } from "zod";

import {
  DOMAINS,
  type LevelDefinition
} from "../contracts";
import {
  DIFFICULTIES,
  boundedTextSchema,
  difficultySchema,
  domainSchema,
  hasUniqueValues,
  kebabCaseIdentifierSchema,
  levelIdSchema,
  localResourceReferenceSchema
} from "./common";

export const ACTIVITIES_PER_DOMAIN = 3;
export const LEVELS_PER_ACTIVITY = DIFFICULTIES.length;
export const LEVELS_PER_DOMAIN =
  ACTIVITIES_PER_DOMAIN * LEVELS_PER_ACTIVITY;
export const TOTAL_LEVELS = DOMAINS.length * LEVELS_PER_DOMAIN;

const objectivesSchema = z
  .array(boundedTextSchema(160))
  .min(1)
  .max(10)
  .refine((objectives) => hasUniqueValues(objectives), {
    message: "Objectives must be unique"
  });

const assetsSchema = z
  .array(localResourceReferenceSchema)
  .max(100)
  .refine((assets) => hasUniqueValues(assets), {
    message: "Asset references must be unique"
  });

export function createLevelDefinitionSchema<TPayloadSchema extends z.ZodType>(
  payloadSchema: TPayloadSchema
) {
  return z
    .strictObject({
      id: levelIdSchema,
      domain: domainSchema,
      activityType: kebabCaseIdentifierSchema,
      difficulty: difficultySchema,
      objectives: objectivesSchema,
      title: boundedTextSchema(80),
      instruction: boundedTextSchema(300),
      promptAudio: localResourceReferenceSchema.optional(),
      assets: assetsSchema,
      payload: payloadSchema
    })
    .superRefine((level, context) => {
      const expectedId =
        level.domain + "-" + level.activityType + "-" + level.difficulty;

      if (level.id !== expectedId) {
        context.addIssue({
          code: "custom",
          path: ["id"],
          message: "Expected level identifier " + expectedId
        });
      }
    });
}

export const levelDefinitionSchema =
  createLevelDefinitionSchema(z.unknown()) satisfies z.ZodType<LevelDefinition>;

export const levelRegistrySchema = z
  .array(levelDefinitionSchema)
  .length(TOTAL_LEVELS)
  .refine((levels) => hasUniqueValues(levels.map(({ id }) => id)), {
    message: "Level identifiers must be unique"
  })
  .superRefine((levels, context) => {
    for (const domain of DOMAINS) {
      const domainLevels = levels.filter((level) => level.domain === domain);

      if (domainLevels.length !== LEVELS_PER_DOMAIN) {
        context.addIssue({
          code: "custom",
          message:
            "Domain " + domain + " must contain exactly " +
            LEVELS_PER_DOMAIN + " levels"
        });
      }

      const activityTypes = new Map<string, typeof domainLevels>();

      for (const level of domainLevels) {
        const activityLevels = activityTypes.get(level.activityType) ?? [];
        activityLevels.push(level);
        activityTypes.set(level.activityType, activityLevels);
      }

      if (activityTypes.size !== ACTIVITIES_PER_DOMAIN) {
        context.addIssue({
          code: "custom",
          message:
            "Domain " + domain + " must contain exactly " +
            ACTIVITIES_PER_DOMAIN + " activity types"
        });
      }

      for (const [activityType, activityLevels] of activityTypes) {
        const difficulties = activityLevels.map(({ difficulty }) => difficulty);

        if (
          activityLevels.length !== LEVELS_PER_ACTIVITY ||
          !hasUniqueValues(difficulties) ||
          !DIFFICULTIES.every((difficulty) =>
            difficulties.includes(difficulty)
          )
        ) {
          context.addIssue({
            code: "custom",
            message:
              "Activity " + domain + "/" + activityType +
              " must contain difficulties 1 through 6 exactly once"
          });
        }
      }
    }
  });
